import type { Client } from '@accounts/transport';
import type { Invitation, Member, Membership, Role, Sent } from './types';

/** What an invited person is shown: before accepting, or, to the person who accepted, after */
export interface Preview {
	organization: string;
	name: string;
	role: Role;
	email: string;
	inviter?: string;
	product?: string;
	state: 'pending' | 'accepted';
}

/**
 * Organizations, their members and their invitations.
 *
 * Nothing here decides anything: every rule that matters — the last owner, who may change ownership,
 * whether an invitation may be accepted — is enforced by the service, and this class carries the
 * refusal back so the interface can explain it. An interface that only hides a disabled button is not
 * a rule.
 */
export class Organizations {
	/**
	 * As many addresses as the service invites at once: its own `Invites.LIMIT`, which `invites.test.tsx`
	 * reads from the service's source so the two cannot drift apart
	 */
	static LIMIT = 20;

	#client: Client;

	constructor(client: Client) {
		this.#client = client;
	}

	async mine(): Promise<Membership[]> {
		return (await this.#client.get<{ organizations: Membership[] }>('/v1/organizations')).organizations;
	}

	/**
	 * @param duplicate Creates it even when the person already has an organization of that name, after
	 * the service warned (`NAME_DUPLICATE`, ACC-20)
	 */
	async create(name: string, duplicate = false): Promise<{ id: string; name: string }> {
		return (await this.#client.post<{ organization: { id: string; name: string } }>('/v1/organizations', { name, ...(duplicate ? { duplicate } : {}) })).organization;
	}

	/**
	 * Changes an organization's name, its short name or both, all or nothing. Only an owner changes the
	 * short name, and the service may ask for a recent proof of identity first (`REAUTHENTICATION_REQUIRED`).
	 *
	 * @param duplicate Saves a name the person already uses for another organization, after the service
	 * warned (`NAME_DUPLICATE`, ACC-20)
	 */
	async change(organization: string, changes: { name?: string; slug?: string }, duplicate = false): Promise<{ id: string; name: string; slug?: string }> {
		return (await this.#client.patch<{ organization: { id: string; name: string; slug?: string } }>(`/v1/organizations/${organization}`, { ...changes, ...(duplicate ? { duplicate } : {}) })).organization;
	}

	async members(organization: string): Promise<Member[]> {
		return (await this.#client.get<{ members: Member[] }>(`/v1/organizations/${organization}/members`)).members;
	}

	async assign(organization: string, account: string, role: Role): Promise<Member> {
		return (await this.#client.patch<{ membership: Member }>(`/v1/organizations/${organization}/members/${account}`, { role })).membership;
	}

	async remove(organization: string, account: string): Promise<void> {
		// Confirmed with its consequence: never sent again after a renewal, the person presses it again
		await this.#client.delete<void>(`/v1/organizations/${organization}/members/${account}`, { replay: false });
	}

	/** The open invitations, or all of them with `all` */
	async invitations(organization: string, all = false): Promise<Invitation[]> {
		return (await this.#client.get<{ invitations: Invitation[] }>(`/v1/organizations/${organization}/invitations${all ? '?state=all' : ''}`)).invitations;
	}

	/**
	 * Invites several addresses at once (ARR-03, ACC-16): each is mailed its one-time link, which comes
	 * back here exactly once and is not stored anywhere
	 *
	 * @param product The product the person was sent from, named in the message
	 */
	async send(organization: string, emails: string[], role: Role, product?: string): Promise<Sent[]> {
		const answer = await this.#client.post<{ results: (Sent & { invitation?: Invitation })[] }>(`/v1/organizations/${organization}/invitations`, { emails, role, ...(product ? { product } : {}) });
		return answer.results.map(result => ({ email: result.email, secret: result.secret, delivery: result.delivery, replaced: result.replaced, error: result.error }));
	}

	/** Sends an invitation again with a new link; the old one stops working */
	async resend(organization: string, invitation: string): Promise<Sent> {
		const answer = await this.#client.post<{ invitation: Invitation; secret: string; delivery: string }>(`/v1/organizations/${organization}/invitations/${invitation}/resend`, {}, { replay: false });
		return { email: answer.invitation.email, secret: answer.secret, delivery: answer.delivery };
	}

	/**
	 * Invites an address.
	 *
	 * The secret comes back exactly once and is not stored anywhere, here or at the service. The screen
	 * that shows it has to say so, because a person who closes it cannot get it back.
	 */
	async invite(organization: string, email: string, role: Role): Promise<{ invitation: Invitation; secret: string }> {
		return this.#client.post<{ invitation: Invitation; secret: string }>(`/v1/organizations/${organization}/invitations`, { email, role });
	}

	async withdraw(organization: string, invitation: string): Promise<void> {
		await this.#client.delete<void>(`/v1/organizations/${organization}/invitations/${invitation}`, { replay: false });
	}

	/** What an invited person is shown before they accept. Open: they have no session yet */
	async preview(secret: string): Promise<Preview> {
		return (await this.#client.get<{ invitation: Preview }>(`/v1/invitations/${encodeURIComponent(secret)}`, { ended: 'caller' })).invitation;
	}

	async accept(secret: string): Promise<{ organization: string; role: Role; product?: string }> {
		return this.#client.post<{ organization: string; role: Role; product?: string }>(`/v1/invitations/${encodeURIComponent(secret)}/acceptance`);
	}

	/** The invitations waiting for the signed-in person (ARR-03) */
	async received(): Promise<Invitation[]> {
		return (await this.#client.get<{ invitations: Invitation[] }>('/v1/invitations')).invitations;
	}

	async take(invitation: string): Promise<{ organization: string; role: Role; product?: string }> {
		return this.#client.post<{ organization: string; role: Role; product?: string }>(`/v1/invitations/received/${encodeURIComponent(invitation)}/acceptance`);
	}

	async decline(invitation: string): Promise<void> {
		await this.#client.post(`/v1/invitations/received/${encodeURIComponent(invitation)}/decline`, {}, { replay: false });
	}
}
